import { Component, Input } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors
} from '@angular/forms';

interface FormDebugRow {
  path: string;
  status: string;
  valueText: string;
  errorsText: string;
  blocking: boolean;
}

@Component({
  selector: 'app-form-debug',
  templateUrl: './form-debug.component.html',
  styleUrls: ['./form-debug.component.scss']
})
export class FormDebugComponent {
  @Input() form: AbstractControl | null = null;
  @Input() title = 'Formularz';

  /** Domyślnie nie pokazujemy wartości pól (mogą zawierać dane osobowe). */
  @Input() showValues = false;

  /** Pokazuje również wyłączone kontrolki; nie blokują one zapisu. */
  @Input() showDisabled = false;

  /** Opcjonalnie porównuje form.valid z flagą używaną w [disabled] przycisku. */
  @Input() externalValid: boolean | null = null;

  checked = false;
  formStatus = 'BRAK FORMULARZA';
  formValid = false;
  externalValidAtCheck: boolean | null = null;
  rows: FormDebugRow[] = [];

  get blockingCount(): number {
    return this.rows.filter(row => row.blocking).length;
  }

  inspect(): void {
    this.checked = true;
    this.rows = [];
    this.externalValidAtCheck = this.externalValid;

    if (!this.form) {
      this.formStatus = 'BRAK FORMULARZA';
      this.formValid = false;
      console.warn('[FormDebug] Nie przekazano kontrolki przez [form].');
      return;
    }

    // Odczytujemy stan formularza bez zmiany walidatorów i bez dotykania pól.
    this.formStatus = this.form.status;
    this.formValid = this.form.valid;
    this.collect(this.form, '');

    console.groupCollapsed(`[FormDebug] ${this.title} — ${this.formStatus}`);
    console.log('form.status:', this.formStatus);
    console.log('form.valid:', this.formValid);
    if (this.externalValidAtCheck !== null) {
      console.log('Flaga z komponentu nadrzędnego:', this.externalValidAtCheck);
      if (this.externalValidAtCheck !== this.formValid) {
        console.warn('Flaga w rodzicu różni się od aktualnego form.valid.');
      }
    }
    console.log('Liczba kontrolek blokujących:', this.blockingCount);
    if (this.rows.length > 0) {
      console.table(this.rows.map(row => ({
        field: row.path,
        status: row.status,
        value: row.valueText,
        errors: row.errorsText,
        blocksSave: row.blocking
      })));
    } else {
      console.log('Brak błędnych lub oczekujących kontrolek.');
    }
    console.groupEnd();
  }

  private collect(control: AbstractControl, path: string): void {
    const displayPath = path || '(formularz główny)';

    if (control.disabled) {
      if (this.showDisabled) {
        this.rows.push(this.makeRow(displayPath, control, false));
      }
      // Wyłączona grupa i jej dzieci nie uczestniczą w walidacji.
      return;
    }

    // Uwaga: FormGroup może być INVALID tylko z powodu dzieci.
    // Dodajemy ją osobno wyłącznie przy błędzie grupowym lub PENDING.
    if (control.errors || control.pending) {
      this.rows.push(this.makeRow(displayPath, control, true));
    }

    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach(key => {
        const childPath = path ? `${path}.${key}` : key;
        this.collect(control.controls[key], childPath);
      });
    } else if (control instanceof FormArray) {
      control.controls.forEach((child, index) => {
        const childPath = `${path}[${index}]`;
        this.collect(child, childPath);
      });
    }
  }

  private makeRow(path: string, control: AbstractControl, blocking: boolean): FormDebugRow {
    return {
      path,
      status: control.status,
      valueText: this.showValues ? this.stringify(control.value) : '(ukryta)',
      errorsText: this.stringify(control.errors),
      blocking
    };
  }

  private stringify(value: unknown): string {
    if (value === undefined) {
      return 'undefined';
    }
    try {
      const serialized = JSON.stringify(value);
      const output = serialized === undefined ? String(value) : serialized;
      return output.length > 240 ? `${output.slice(0, 237)}...` : output;
    } catch (_error) {
      return '[wartość niemożliwa do serializacji]';
    }
  }
}
